import { Component, OnInit } from "@angular/core";
import { FormGroup, FormBuilder, Validators } from "@angular/forms";

import { SAML } from "@impulse/sdp-shared-models";

import { SamlAliasesService } from "../../saml-aliases.service";
import { AbstractWizardStepComponent } from "../../../wizard-dialog/abstract-wizard-step.component";
import { TranslateService, TranslationSubstitutions } from "../../../../common/translate";
import { ApiGatewayService } from "../../../../common/api-gateway/api-gateway.service";
import { SnackbarService } from "../../../../common/snackbar/snackbar.service";
import { shortenPhrase } from "../../../../util";

@Component({
	templateUrl: "./saml-alias-edit.component.html"
})
export class SamlAliasEditComponent extends AbstractWizardStepComponent<void> implements OnInit {
	public form!: FormGroup;
	public isCreate = this.service.isCreate;
	public providerData: SAML.IdentityProvider[] = [];
	public label = "";
	private _showProgressBar = false;
	public placeholder = this.translateService.translate("saml-alias.form.label.alias");

	private id = "";

	constructor(
		protected service: SamlAliasesService,
		private formBuilder: FormBuilder,
		private apiGatewayService: ApiGatewayService,
		private translateService: TranslateService,
		private snackbarService: SnackbarService
	) {
		super();
	}

	public async ngOnInit() {
		this.form = this.formBuilder.group({
			samlGroup: [
				this.service.getValue("samlGroup", ""),
				Validators.compose([
					Validators.required,
					Validators.pattern("^[a-zA-Z0-9 _,.=-]*$"),
					Validators.maxLength(256)
				])
			],
			label: [
				this.service.getValue("label", ""),
				Validators.compose([
					Validators.pattern("^[a-zA-Z0-9 _,.=-]*$"),
					Validators.maxLength(256)
				])
			],
			providerId: [
				this.service.getValue("providerId", ""),
				Validators.compose([
					Validators.required
				])
			],
		});
		try {
			this.providerData = await this.apiGatewayService.getIdentityProviders();
			// If only one provider, then preset the value to it
			if (this.providerData.length === 1) {
				this.form.patchValue({
					providerId: this.providerData[0].id
				});
				this.form.controls["providerId"].disable();
			}
		} catch (e) {
			this.snackbarService.open(this.translateService.translate(e));
		}
	}

	public resetDuplicateGroupError() {
		if (!!this.form.controls["samlGroup"].errors) {
			this.form.controls["samlGroup"].updateValueAndValidity();
		}
	}

	public translateWithReplacement(message: string, replacements: TranslationSubstitutions) {
		return this.translateService.translate(message, replacements);
	}

	public getDisplayDupeErrorMessage() {
		const displayName = shortenPhrase(this.form.value.label.length > 0 ? this.form.value.label : this.form.value.samlGroup, 15);
		const replacement = { displayName: displayName };
		return this.translateWithReplacement("error.saml-alias.saml-label.duplicate", replacement);

	}

	public getGroupDupeErrorMessage() {
		const providerName = shortenPhrase(this.providerData.length === 1 ?
			this.providerData[0].name : this.service.getProviderNameById(this.providerData, this.form.value.providerId), 15);
		const group = shortenPhrase(this.form.value.samlGroup, 15);
		return this.translateWithReplacement("error.saml-alias.saml-group.duplicate", { group: group, idp: providerName });
	}

	public get title() {
		return this.isCreate ? "saml-alias.wizard.edit.title.create" : "saml-alias.wizard.edit.title.update";
	}

	public get description() {
		return this.isCreate ? "saml-alias.wizard.edit.description.create" : "saml-alias.wizard.edit.description.update";
	}

	public get addClass() {
		return "sdp-group-edit-dialog-container";
	}

	public get nextStep(): AbstractWizardStepComponent.ConstructorFunction<void> | undefined {
		return undefined;
	}

	public get previousStep(): AbstractWizardStepComponent.ConstructorFunction<void> | undefined {
		return undefined;
	}

	public get isFormValid() {
		return this.form && this.form.valid;
	}

	public async performPrevious() {
		// Noop
	}

	public async performNext() {
		if (this.form.valid) {
			await this.saveSamlAlias();
			this.service.closeDialog();
		}
	}

	public get showProgressBar() {
		return this._showProgressBar;
	}

	protected async saveSamlAlias() {
		this._showProgressBar = true;
		try {
			if (this.form !== undefined) {
				const message = await this.service.processSamlAliasWizardEdit(this.form.getRawValue(), this.id);
				if (message.length > 0) {
					this.snackbarService.open(message);
				}
			}
		} catch (e) {
			if (e.error.message === "error.saml-alias.saml-group.duplicate") {
				// if error is duplicate show error and keep form open.
				this.form.controls["samlGroup"].setErrors({
					duplicate: "error.saml-alias.saml-group.duplicate"
				});
				// angular needs to think the value was modifed to fire error event
				this.form.controls["samlGroup"].markAsTouched();
				this.form.controls["samlGroup"].markAsDirty();
			} else if (e.error.message === "error.saml-alias.saml-label.duplicate") {
				// if error is duplicate show error and keep form open.
				this.form.controls["label"].setErrors({
					duplicate: "error.saml-alias.saml-label.duplicate"
				});
				// angular needs to think the value was modifed to fire error event
				this.form.controls["label"].markAsTouched();
				this.form.controls["label"].markAsDirty();
			} else {
				this.snackbarService.open(this.translateService.translate(e));
			}
			throw e;
		} finally {
			this._showProgressBar = false;
		}

	}
}
